import ScrollReveal from '../ScrollReveal';
import CustomizationPanel from './CustomizationPanel';
import type { FontOption, ColorOption, GradientOption, DensityOption } from './CustomizationPanel';
import useObfuscatedEmail from '@/hooks/useObfuscatedEmail';
import { t } from '@/data/translations';
import type { Lang } from '@/pages/Index';

interface HeaderProps {
  font: FontOption;
  color: ColorOption;
  gradient: GradientOption;
  density: DensityOption;
  lang: Lang;
  onFontChange: (font: FontOption) => void;
  onColorChange: (color: ColorOption) => void;
  onGradientChange: (gradient: GradientOption) => void;
  onDensityChange: (density: DensityOption) => void;
  onLangChange: (lang: Lang) => void;
}

const Header = ({
  font, color, gradient, density, lang,
  onFontChange, onColorChange, onGradientChange, onDensityChange, onLangChange,
}: HeaderProps) => {
  const { displayEmail, handleEmailClick } = useObfuscatedEmail();
  const tr = t(lang).header;
  return (
    <header id="header" className="flex flex-col items-center justify-center px-6 md:px-12 lg:px-24 relative overflow-hidden">
      <div className="absolute inset-0 hero-gradient animate-gradient-shift" aria-hidden="true" />
      
      <div className="max-w-4xl w-full relative z-10">
        <ScrollReveal>
          <div className="flex items-center justify-between mb-6">
            <p className="text-muted-foreground text-sm tracking-widest uppercase">Curriculum Vitæ</p>
            <div className="flex items-center gap-1 text-sm tracking-wider">
              <button
                onClick={() => onLangChange('fr')}
                className={`px-2 py-1 transition-colors duration-300 cursor-pointer ${lang === 'fr' ? 'text-foreground font-medium' : 'text-muted-foreground/50 hover:text-muted-foreground'}`}
              >FR</button>
              <span className="text-muted-foreground/30">/</span>
              <button
                onClick={() => onLangChange('en')}
                className={`px-2 py-1 transition-colors duration-300 cursor-pointer ${lang === 'en' ? 'text-foreground font-medium' : 'text-muted-foreground/50 hover:text-muted-foreground'}`}
              >EN</button>
            </div>
          </div>
        </ScrollReveal>
        
        <ScrollReveal delay={100}>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-semibold tracking-tight text-foreground mb-8 text-balance">
            Allen Le Yaouanc
          </h1>
        </ScrollReveal>
        
        <ScrollReveal delay={200}>
          <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 text-lg md:text-xl text-muted-foreground">
            <span>{tr.subtitle}</span>
            <span className="hidden md:block w-1 h-1 bg-primary rounded-full" />
            <span>{tr.subtitleSuffix}</span>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={300}>
          <div className="mt-16 flex flex-col sm:flex-row sm:items-center gap-3 text-base md:text-lg text-muted-foreground">
            <a href="#" onClick={handleEmailClick} className="link-hover w-fit">{displayEmail}</a>
            <span className="hidden sm:block">—</span>
            <span>{tr.location}</span>
          </div>
        </ScrollReveal>

        <CustomizationPanel
          currentFont={font} currentColor={color} currentGradient={gradient} currentDensity={density}
          onFontChange={onFontChange} onColorChange={onColorChange} onGradientChange={onGradientChange} onDensityChange={onDensityChange}
          lang={lang}
        />

        <ScrollReveal delay={600}>
          <div className="mt-12 flex justify-center">
            <button 
              onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
              className="cursor-pointer hover:text-primary transition-colors duration-300"
              aria-label={tr.scrollLabel}
            >
              <svg className="w-6 h-6 text-muted-foreground/50 hover:text-primary transition-colors duration-300 arrow-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </button>
          </div>
        </ScrollReveal>
      </div>
    </header>
  );
};

export default Header;
